import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import BenevolCheckLogo from '../components/BenevolCheckLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateCertificatePage = () => {
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [createdCertificate, setCreatedCertificate] = useState(null);
  const [sendingValidationEmail, setSendingValidationEmail] = useState(false);
  const [validationEmailSent, setValidationEmailSent] = useState(false);
  const [validationEmailError, setValidationEmailError] = useState('');
  const [approvedAssociations, setApprovedAssociations] = useState([]);
  
  const [formData, setFormData] = useState({
    volunteer_name: user?.role === 'volunteer' ? user.name : '',
    volunteer_email: user?.role === 'volunteer' ? user.email : '',
    association_name: user?.role === 'association_admin' ? user.association_name : '',
    signatory_name: user?.role === 'association_admin' ? user.name : '',
    signatory_email: '',
    start_date: '',
    end_date: '',
    hours_volume: '',
    mission_description: '',
    competencies: {
      learning_ability: false,
      autonomy: false,
      adaptability: false,
      creativity: false,
      sociability: false,
      cooperation: false,
      empathy: false,
      leadership: false,
      rigor_reliability: false,
      responsibility: false,
      curiosity: false,
      engagement: false
    }
  });

  // Charger la liste des associations validées pour les bénévoles
  useEffect(() => {
    const fetchApprovedAssociations = async () => {
      if (user?.role === 'volunteer') {
        try {
          const response = await axios.get(`${API}/associations/approved`);
          setApprovedAssociations(response.data);
        } catch (error) {
          console.error('Erreur lors du chargement des associations:', error);
        }
      }
    };
    fetchApprovedAssociations();
  }, [user]);

  const handleAssociationSelect = (associationName) => {
    const selectedAssoc = approvedAssociations.find(a => a.association_name === associationName);
    if (selectedAssoc) {
      setFormData({
        ...formData,
        association_name: selectedAssoc.association_name,
        signatory_name: selectedAssoc.name,
        signatory_email: selectedAssoc.email
      });
    }
  };

  const handleCompetencyChange = (key, checked) => {
    setFormData({
      ...formData,
      competencies: {
        ...formData.competencies,
        [key]: checked
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/certificates`,
        formData,
        { headers: getAuthHeader() }
      );
      
      setCreatedCertificate(response.data);
      setSuccess(true);
      
      // If admin created it, redirect after delay. If volunteer, show options
      if (user?.role === 'association_admin') {
        setTimeout(() => {
          navigate(`/certificate/${response.data.id}`);
        }, 1500);
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Erreur lors de la création de l\'attestation');
      setLoading(false);
    }
  };

  const handleSendValidationEmail = async () => {
    if (!createdCertificate) return;
    
    setSendingValidationEmail(true);
    setValidationEmailError('');
    
    try {
      await axios.post(
        `${API}/certificates/${createdCertificate.id}/send-validation-request`,
        {},
        { headers: getAuthHeader() }
      );
      setValidationEmailSent(true);
    } catch (error) {
      setValidationEmailError(error.response?.data?.detail || 'Erreur lors de l\'envoi de l\'email');
    } finally {
      setSendingValidationEmail(false);
    }
  };

  const competenciesConfig = [
    {
      title: 'Dans l\'action',
      items: [
        { key: 'learning_ability', label: 'Capacité d\'apprentissage', desc: 'il/elle a bien compris en quoi consistait sa mission pour en maîtriser la pratique' },
        { key: 'autonomy', label: 'Autonomie', desc: 'il/elle a exercé son activité sans avoir besoin d\'une supervision' },
        { key: 'adaptability', label: 'Adaptabilité', desc: 'il/elle a réagi avec pertinence pour modifier sa façon de faire face aux problèmes rencontrés' },
        { key: 'creativity', label: 'Créativité', desc: 'il/elle a proposé des idées pour rendre son activité plus efficace ou plus conviviale' }
      ]
    },
    {
      title: 'Dans l\'équipe',
      items: [
        { key: 'sociability', label: 'Sociabilité', desc: 'il/elle a facilement trouvé sa place parmi les autres membres du groupe' },
        { key: 'cooperation', label: 'Coopération', desc: 'dans son action, il/elle a tenu compte de l\'activité des autres membres de son équipe' },
        { key: 'empathy', label: 'Empathie', desc: 'dans les moments de tension, il/elle a su se mettre à la place de l\'autre pour comprendre son point de vue et éviter les conflits' },
        { key: 'leadership', label: 'Leadership', desc: 'il/elle a montré des capacités pour motiver l\'activité des autres et les solliciter en leur apportant si nécessaire un conseil ou un appui' }
      ]
    },
    {
      title: 'Dans l\'association',
      items: [
        { key: 'rigor_reliability', label: 'Rigueur et fiabilité', desc: 'il/elle a exercé son activité dans le respect des règles, de pratiques et des valeurs de l\'association' },
        { key: 'responsibility', label: 'Responsabilité', desc: 'il/elle s\'est senti(e) personnellement concerné(e) par la bonne réalisation des tâches ou la conduite des projets jusqu\'à leur accomplissement' },
        { key: 'curiosity', label: 'Curiosité', desc: 'il/elle s\'est intéressé(e) à la vie de l\'association, à son projet associatif et à ses diverses activités' },
        { key: 'engagement', label: 'Engagement', desc: 'il/elle s\'est montré(e) prêt(e) à prendre des responsabilités dans l\'animation, la vie collective ou le développement de l\'association' }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
            <BenevolCheckLogo size="md" />
            <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard" className="w-full sm:w-auto">
              ← Retour
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 sm:py-8 max-w-4xl">
        <Card data-testid="create-certificate-form">
          <CardHeader>
            <CardTitle className="text-xl sm:text-2xl">Créer une attestation</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Success screen for volunteers */}
            {success && user?.role === 'volunteer' && (
              <div className="space-y-6" data-testid="success-screen">
                <Alert className="bg-green-50 border-green-200">
                  <AlertDescription className="text-green-800 text-sm sm:text-base">
                    ✅ Attestation créée avec succès !
                  </AlertDescription>
                </Alert>
                
                <div className="bg-indigo-50 p-6 rounded-lg text-center">
                  <h3 className="text-lg font-semibold text-indigo-900 mb-4">
                    📋 Votre attestation est en attente de validation
                  </h3>
                  <p className="text-gray-700 mb-6">
                    Pour que votre attestation soit validée, le responsable de l'association doit la confirmer.
                  </p>
                  
                  {formData.signatory_email && !validationEmailSent && (
                    <div className="space-y-4">
                      <p className="text-sm text-gray-600">
                        Envoyer l'attestation à : <strong>{formData.signatory_email}</strong>
                      </p>
                      
                      {validationEmailError && (
                        <Alert variant="destructive" className="text-left">
                          <AlertDescription>{validationEmailError}</AlertDescription>
                        </Alert>
                      )}
                      
                      <Button
                        onClick={handleSendValidationEmail}
                        disabled={sendingValidationEmail}
                        className="bg-indigo-600 hover:bg-indigo-700"
                        data-testid="send-validation-email-button"
                      >
                        {sendingValidationEmail ? (
                          '📤 Envoi en cours...'
                        ) : (
                          '📧 Envoyer au responsable associatif'
                        )}
                      </Button>
                    </div>
                  )}
                  
                  {validationEmailSent && (
                    <Alert className="bg-green-50 border-green-200 text-left">
                      <AlertDescription className="text-green-800">
                        ✅ Attestation envoyée au responsable associatif !
                        <br />
                        <span className="text-sm">Un email a été envoyé à <strong>{formData.signatory_email}</strong> pour valider votre attestation.</span>
                      </AlertDescription>
                    </Alert>
                  )}
                  
                  {!formData.signatory_email && !validationEmailSent && (
                    <Alert className="bg-yellow-50 border-yellow-200 text-left">
                      <AlertDescription className="text-yellow-800">
                        ⚠️ Aucun email de responsable n'a été renseigné. 
                        Vous pouvez copier le lien de validation depuis la page de votre attestation.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/certificate/${createdCertificate?.id}`)}
                    data-testid="view-certificate-button"
                  >
                    📄 Voir mon attestation
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/dashboard')}
                    data-testid="go-to-dashboard-button"
                  >
                    🏠 Retour au tableau de bord
                  </Button>
                </div>
              </div>
            )}
            
            {/* Success message for admins */}
            {success && user?.role === 'association_admin' && (
              <Alert className="mb-4 sm:mb-6 bg-green-50 border-green-200" data-testid="success-alert">
                <AlertDescription className="text-green-800 text-sm sm:text-base">
                  ✅ Attestation créée avec succès ! Redirection...
                </AlertDescription>
              </Alert>
            )}
            
            {error && (
              <Alert variant="destructive" className="mb-4 sm:mb-6" data-testid="error-alert">
                <AlertDescription className="text-sm sm:text-base">{error}</AlertDescription>
              </Alert>
            )}

            {/* Only show form if not in success state for volunteers */}
            {(!success || user?.role === 'association_admin') && (
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-base sm:text-lg border-b pb-2">Informations générales</h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="volunteer_name">Nom du bénévole</Label>
                    <Input
                      id="volunteer_name"
                      value={formData.volunteer_name}
                      onChange={(e) => setFormData({...formData, volunteer_name: e.target.value})}
                      required
                      data-testid="volunteer-name-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="volunteer_email">Email du bénévole</Label>
                    <Input
                      id="volunteer_email"
                      type="email"
                      value={formData.volunteer_email}
                      onChange={(e) => setFormData({...formData, volunteer_email: e.target.value})}
                      required
                      data-testid="volunteer-email-input"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="association_name">Nom de l'association</Label>
                  {user?.role === 'volunteer' && approvedAssociations.length > 0 ? (
                    <Select
                      value={formData.association_name}
                      onValueChange={handleAssociationSelect}
                    >
                      <SelectTrigger className="w-full" data-testid="association-name-select">
                        <SelectValue placeholder="Sélectionnez une association" />
                      </SelectTrigger>
                      <SelectContent>
                        {approvedAssociations.map((assoc, idx) => (
                          <SelectItem key={idx} value={assoc.association_name}>
                            {assoc.association_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      id="association_name"
                      value={formData.association_name}
                      onChange={(e) => setFormData({...formData, association_name: e.target.value})}
                      required
                      data-testid="association-name-input"
                    />
                  )}
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="signatory_name">Nom du signataire (responsable)</Label>
                    <Input
                      id="signatory_name"
                      value={formData.signatory_name}
                      onChange={(e) => setFormData({...formData, signatory_name: e.target.value})}
                      required
                      readOnly={user?.role === 'volunteer' && formData.association_name !== ''}
                      className={user?.role === 'volunteer' && formData.association_name !== '' ? 'bg-gray-100' : ''}
                      data-testid="signatory-name-input"
                    />
                  </div>
                  
                  {user?.role === 'volunteer' && (
                    <div>
                      <Label htmlFor="signatory_email">Email du responsable <span className="text-red-500">*</span></Label>
                      <Input
                        id="signatory_email"
                        type="email"
                        value={formData.signatory_email}
                        onChange={(e) => setFormData({...formData, signatory_email: e.target.value})}
                        placeholder="responsable@association.fr"
                        required
                        readOnly={user?.role === 'volunteer' && formData.association_name !== ''}
                        className={user?.role === 'volunteer' && formData.association_name !== '' ? 'bg-gray-100' : ''}
                        data-testid="signatory-email-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {formData.association_name ? 'Email rempli automatiquement' : 'Un email de validation sera envoyé automatiquement à cette adresse'}
                      </p>
                    </div>
                  )}
                </div>
                
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="start_date">Date de début</Label>
                    <Input
                      id="start_date"
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                      required
                      data-testid="start-date-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="end_date">Date de fin</Label>
                    <Input
                      id="end_date"
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                      required
                      data-testid="end-date-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="hours_volume">Volume horaire</Label>
                    <Input
                      id="hours_volume"
                      type="number"
                      min="1"
                      value={formData.hours_volume}
                      onChange={(e) => setFormData({...formData, hours_volume: parseInt(e.target.value)})}
                      required
                      placeholder="20"
                      data-testid="hours-volume-input"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="mission_description">Description de la mission</Label>
                  <Textarea
                    id="mission_description"
                    value={formData.mission_description}
                    onChange={(e) => setFormData({...formData, mission_description: e.target.value})}
                    required
                    rows={3}
                    placeholder="Brève description de l'activité bénévole..."
                    data-testid="mission-description-input"
                  />
                </div>
              </div>

              {/* Competencies */}
              <div className="space-y-4 sm:space-y-6">
                <h3 className="font-semibold text-base sm:text-lg border-b pb-2">Compétences observées</h3>
                <p className="text-xs sm:text-sm text-gray-600">
                  Cochez les cases correspondant aux comportements observés au cours de la mission.
                </p>
                
                {competenciesConfig.map((category, idx) => (
                  <div key={idx} className="space-y-3">
                    <h4 className="font-semibold text-sm sm:text-base text-indigo-900">{category.title}</h4>
                    <div className="space-y-3 pl-2 sm:pl-4">
                      {category.items.map((item) => (
                        <div key={item.key} className="flex items-start space-x-2 sm:space-x-3">
                          <Checkbox
                            id={item.key}
                            checked={formData.competencies[item.key]}
                            onCheckedChange={(checked) => handleCompetencyChange(item.key, checked)}
                            data-testid={`competency-${item.key}`}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <Label 
                              htmlFor={item.key} 
                              className="font-medium cursor-pointer text-sm sm:text-base"
                            >
                              {item.label}
                            </Label>
                            <p className="text-xs sm:text-sm text-gray-600 mt-1">{item.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Submit */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => navigate('/dashboard')}
                  data-testid="cancel-button"
                  className="w-full sm:w-auto order-2 sm:order-1"
                >
                  Annuler
                </Button>
                <Button 
                  type="submit" 
                  disabled={loading}
                  data-testid="submit-button"
                  className="w-full sm:w-auto order-1 sm:order-2"
                >
                  {loading ? 'Création...' : 'Créer l\'attestation'}
                </Button>
              </div>
            </form>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default CreateCertificatePage;